<template>
  <b-sidebar
    :visible="value"
    @change="onVisibleChange"
    right
    shadow
    backdrop
    width="400px"
    bg-variant="white"
    no-header
    sidebar-class="bulk-actions-sidebar"
  >
    <div class="bulk-actions-sidebar__inner">
      <!-- Header -->
      <div class="bulk-actions-sidebar__header d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
        <div class="d-flex align-items-center">
          <b-button
            v-if="selectedAction"
            variant="link"
            size="sm"
            class="p-0 mr-2 text-dark"
            @click="goBack"
          >
            <i class="fa fa-arrow-left"></i>
          </b-button>
          <span class="font-weight-bold">
            {{ selectedAction ? actionTypeLabel(selectedAction.actionType) : 'Bulk Actions' }}
          </span>
        </div>
        <b-button variant="link" size="sm" class="p-0 text-dark" @click="close">
          <i class="fa fa-times"></i>
        </b-button>
      </div>

      <!-- List View -->
      <div v-if="!selectedAction" class="bulk-actions-sidebar__body">
        <div v-if="loading" class="text-center py-5">
          <b-spinner variant="primary" />
        </div>
        <div v-else-if="items.length === 0" class="text-center text-muted py-5 px-3">
          <i class="fa fa-tasks fa-2x mb-2 d-block"></i>
          No bulk actions found.
        </div>
        <b-list-group v-else flush>
          <b-list-group-item
            v-for="action in items"
            :key="action.id"
            button
            @click="openDetail(action)"
            class="bulk-actions-sidebar__list-item"
          >
            <div class="d-flex justify-content-between align-items-start">
              <div>
                <div class="font-weight-semibold small">{{ actionTypeLabel(action.actionType) }}</div>
                <div class="text-muted" style="font-size: 0.75rem;">{{ formatDate(action.createDateTimeUtc) }}</div>
              </div>
              <b-badge :variant="statusVariant(action.status)" class="ml-2 flex-shrink-0">
                {{ statusLabel(action.status) }}
              </b-badge>
            </div>
            <div class="mt-1 d-flex" style="font-size: 0.75rem; gap: 8px;">
              <span class="text-success" v-if="action.completedLineItems > 0">
                <i class="fa fa-check-circle"></i> {{ action.completedLineItems }}
              </span>
              <span class="text-danger" v-if="action.failedLineItems > 0">
                <i class="fa fa-times-circle"></i> {{ action.failedLineItems }}
              </span>
              <span class="text-muted" v-if="action.pendingLineItems > 0">
                <i class="fa fa-clock-o"></i> {{ action.pendingLineItems }}
              </span>
              <span class="text-warning" v-if="action.inProgressLineItems > 0">
                <i class="fa fa-spinner fa-spin"></i> {{ action.inProgressLineItems }}
              </span>
            </div>
          </b-list-group-item>
        </b-list-group>
        <div v-if="hasMore" class="text-center py-3">
          <b-button variant="outline-primary" size="sm" :disabled="loadingMore" @click="loadMore">
            <b-spinner v-if="loadingMore" small /> Load More
          </b-button>
        </div>
      </div>

      <!-- Detail View -->
      <div v-else class="bulk-actions-sidebar__body">
        <!-- Result URL (e.g. DownloadAllLabels PDF) -->
        <div v-if="selectedAction.resultUrl" class="px-3 pt-3">
          <a
            :href="selectedAction.resultUrl"
            target="_blank"
            class="btn btn-sm btn-primary btn-block"
          >
            <i class="fa fa-download mr-1"></i> Download PDF
          </a>
        </div>

        <!-- Status summary -->
        <div class="px-3 py-2 d-flex align-items-center justify-content-between border-bottom">
          <b-badge :variant="statusVariant(selectedAction.status)" class="px-2 py-1">
            {{ statusLabel(selectedAction.status) }}
          </b-badge>
          <span class="text-muted" style="font-size: 0.75rem;">{{ formatDate(selectedAction.createDateTimeUtc) }}</span>
        </div>

        <!-- Loading detail -->
        <div v-if="loadingDetail" class="text-center py-5">
          <b-spinner variant="primary" />
        </div>

        <!-- Line items -->
        <b-list-group v-else flush>
          <b-list-group-item
            v-for="item in selectedAction.items"
            :key="item.id"
            class="px-3 py-2"
          >
            <div class="d-flex justify-content-between align-items-center">
              <router-link
                :to="{ name: 'Common.OrderDetails', query: { id: item.orderId } }"
                class="font-weight-semibold small"
                @click.native="close"
              >
                {{ item.orderNumber }}
              </router-link>
              <b-badge :variant="statusVariant(item.status)" class="ml-2 flex-shrink-0">
                {{ statusLabel(item.status) }}
              </b-badge>
            </div>
            <div
              v-if="item.errorMessage"
              class="text-danger mt-1"
              style="font-size: 0.75rem;"
            >
              <i class="fa fa-exclamation-circle"></i> {{ item.errorMessage }}
            </div>
          </b-list-group-item>
          <b-list-group-item v-if="selectedAction.items && selectedAction.items.length === 0" class="text-muted text-center py-3">
            No items found.
          </b-list-group-item>
        </b-list-group>
      </div>
    </div>
  </b-sidebar>
</template>

<script>
import { mapState, mapGetters } from 'vuex';

export default {
  name: 'BulkActionsSidebar',
  props: {
    value: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      loading: false,
      loadingMore: false,
      loadingDetail: false,
      selectedAction: null,
      page: 1,
      perPage: 20,
      total: 0,
      items: [],
      pollInterval: null
    };
  },
  computed: {
    hasMore() {
      return this.items.length < this.total;
    },
    activeCount() {
      return this.items.filter(function(a) {
        return a.status === 'Pending' || a.status === 'InProgress';
      }).length;
    }
  },
  watch: {
    value(val) {
      if (val && !this.loading) {
        this.loadingDetail = false;
        this.selectedAction = null;
        this.loadItems();
      }
    },
    loading(val) {
      this.$emit('update:loading', val);
    },
    activeCount(val) {
      this.$emit('update:activeCount', val);
      if (val > 0) {
        this.startPolling();
      } else {
        this.stopPolling();
      }
    }
  },
  mounted() {
    this.$root.$on('storefront-bulk-action-queued', this.onBulkActionQueued);
    this.loadItems();
  },
  beforeDestroy() {
    this.$root.$off('storefront-bulk-action-queued', this.onBulkActionQueued);
    this.stopPolling();
  },
  methods: {
    onBulkActionQueued() {
      this.loadItems();
    },
    startPolling() {
      if (this.pollInterval) return;
      this.pollInterval = setInterval(() => {
        this.loadItems();
      }, 5000);
    },
    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },
    onVisibleChange(val) {
      this.$emit('input', val);
    },
    close() {
      this.$emit('input', false);
    },
    goBack() {
      this.selectedAction = null;
    },
    loadItems() {
      this.loading = true;
      this.page = 1;
      this.items = [];
      this.$store.dispatch('storefrontBulkActions/fetchItems', { saveToStore: false })
        .then(resp => {
          if (resp && resp.data) {
            this.items = resp.data.items || [];
            this.total = resp.data.total || 0;
          }
          this.loading = false;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    loadMore() {
      this.loadingMore = true;
      this.page++;
      this.$store.dispatch('storefrontBulkActions/fetchItems', { saveToStore: false })
        .then(resp => {
          if (resp && resp.data) {
            this.items = this.items.concat(resp.data.items || []);
            this.total = resp.data.total || 0;
          }
          this.loadingMore = false;
        })
        .catch(() => {
          this.loadingMore = false;
        });
    },
    openDetail(action) {
      this.selectedAction = action;
      this.loadingDetail = true;
      this.$store.dispatch('storefrontBulkActions/getItemById', action.id)
        .then(resp => {
          if (resp && resp.data) {
            this.selectedAction = resp.data;
          }
          this.loadingDetail = false;
        })
        .catch(() => {
          this.loadingDetail = false;
        });
    },
    actionTypeLabel(type) {
      const labels = {
        RemoveFromHold: 'Remove From Hold',
        PlaceOnHold: 'Place On Hold',
        UndoPendingFulfillment: 'Undo Pending Fulfillment',
        CancelOrder: 'Cancel Order',
        ReplaceProductSku: 'Replace Product SKU',
        UpdateReqShipping: 'Update Req. Shipping',
        AddNote: 'Add Note',
        AutoGenerateLabels: 'Auto Generate Labels',
        DownloadAllLabels: 'Download All Labels',
        VoidAllLabels: 'Void All Labels'
      };
      return labels[type] || type;
    },
    statusLabel(status) {
      const labels = {
        Pending: 'Pending',
        InProgress: 'In Progress',
        Completed: 'Completed',
        PartiallyCompleted: 'Partially Completed',
        Failed: 'Failed'
      };
      return labels[status] || status;
    },
    statusVariant(status) {
      const variants = {
        Pending: 'secondary',
        InProgress: 'warning',
        Completed: 'success',
        PartiallyCompleted: 'warning',
        Failed: 'danger'
      };
      return variants[status] || 'secondary';
    },
    formatDate(dateStr) {
      return this.$moment.utc(dateStr).local().fromNow();
    }
  }
};
</script>

<style scoped>
.bulk-actions-sidebar__inner {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.bulk-actions-sidebar__header {
  background: #fff;
  flex-shrink: 0;
}
.bulk-actions-sidebar__body {
  flex: 1;
  overflow-y: auto;
}
.bulk-actions-sidebar__list-item {
  cursor: pointer;
  transition: background 0.15s;
}
.bulk-actions-sidebar__list-item:hover {
  background-color: #f8f9fa;
}
.font-weight-semibold {
  font-weight: 600;
}
</style>
